import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MessageCircle, Loader2, Heart } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Matches() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_paid) {
      navigate('/paywall');
      return;
    }
    fetchMatches();
  }, [user, navigate]);

  const fetchMatches = async () => {
    try {
      const { data } = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(data.matches);
    } catch (e) {
      console.error('Failed to fetch matches:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="px-6 py-6 border-b border-zinc-900">
        <h1 className="font-outfit text-2xl font-semibold text-white">Matches</h1>
        <p className="text-zinc-500 text-sm mt-1">
          {matches.length} {matches.length === 1 ? 'match' : 'matches'}
        </p>
      </header>

      {/* Content */}
      <main className="flex-1 pb-24 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
          </div>
        )}

        {!loading && matches.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
              <Heart className="w-8 h-8 text-zinc-600" />
            </div>
            <h2 className="font-outfit text-lg font-medium text-white mb-2">No matches yet</h2>
            <p className="text-zinc-500 text-sm">
              Keep swiping to find your matches!
            </p>
          </div>
        )}

        {!loading && matches.length > 0 && (
          <div className="divide-y divide-zinc-900">
            {matches.map((match) => (
              <MatchItem
                key={match.match_id}
                match={match}
                onClick={() => navigate(`/app/chat/${match.match_id}`)}
              />
            ))}
          </div>
        )}
      </main>

      <BottomNav active="matches" />
    </div>
  );
}

function MatchItem({ match, onClick }) {
  const { user } = match;
  const photo = user.photos?.[0] || 'https://images.unsplash.com/photo-1694877164163-bd51561f794a?w=100';

  return (
    <button
      data-testid={`match-${match.match_id}`}
      onClick={onClick}
      className="w-full flex items-center gap-4 px-6 py-4 hover:bg-zinc-900/50 transition-colors text-left"
    >
      <div className="relative">
        <img
          src={photo}
          alt={user.name}
          className="w-14 h-14 rounded-full object-cover"
        />
        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-rose-600 flex items-center justify-center">
          <MessageCircle className="w-3 h-3 text-white" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-white truncate">{user.name}</h3>
        <p className="text-zinc-500 text-sm truncate">
          {match.last_message_at ? 'Tap to continue chatting' : 'Start a conversation!'}
        </p>
      </div>
      {!match.last_message_at && (
        <span className="px-2 py-1 rounded-full bg-rose-500/20 text-rose-400 text-xs font-medium">
          New
        </span>
      )}
    </button>
  );
}
